import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as Tabs from '@radix-ui/react-tabs';
import { Copy, FilePlus2, FolderOpen, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import type { ColumnMeta, FieldDef, ListResult, ScreenDef } from '@/lib/types';
import { DataGrid } from '@/grid/DataGrid';
import { LookupDialog } from '@/grid/LookupDialog';
import { CellEditor } from '@/grid/CellEditor';
import { dizaynColumns } from './columns';
import { Spinner, ErrorBox } from '@/ui/Spinner';
import { confirmDialog } from '@/ui/confirm';
import { ReportMenu } from '@/reports/ReportMenu';
import { cn } from '@/lib/cn';

interface Part {
  levelId: number; name: string; caption: string; columns: ColumnMeta[]; rows: Record<string, any>[]; fields: FieldDef[];
  source: ListResult['source']; fk: string | null; hooks: { new: boolean; calc: boolean }; row?: Record<string, any> | null;
}
interface Source { levelId: number; name: string; caption: string; fields: FieldDef[] }
interface Belge { master: Part | null; details: Part[]; sources: Source[] }

/**
 * Dizayn Master/Detail belge ekranı (Delphi Form80_1 / Form81):
 *  Master = DIZAYN_ADI 'Master' (:ID/:ara = belge no), Detail = 'Detail' seviyeleri (:ara = belge no),
 *  belge listesi DIZAYN_LISTE, yeni belge varsayılanı TABLE_NEW, kaydetme öncesi HESAPLAMA, silme TABLE_DELETE,
 *  kaydetme sonrası TABLE_POST, çoğaltma FORM_COGALT.
 */
export function DizaynBelgeScreen({ screen, id }: { screen: ScreenDef; id?: number | null }) {
  const qc = useQueryClient();
  const [docId, setDocId] = useState<number | null>(id ?? null);
  const [pickOpen, setPickOpen] = useState(false);
  const [draft, setDraft] = useState<Record<string, any> | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const def = useQuery({ queryKey: ['screen', screen.tag], queryFn: () => api<ScreenDef>(`/screens/${screen.tag}`), initialData: screen.dizayn ? screen : undefined });
  const belge = useQuery({
    queryKey: ['belge', screen.tag, docId ?? 0],
    queryFn: () => api<Belge>(`/screens/${screen.tag}/dizayn/belge/${docId ?? 0}`),
  });
  const master = belge.data?.master ?? null;
  const rights = screen.rights;

  useEffect(() => {
    if (!belge.data) return;
    if (docId && master?.row) { setDraft({ ...master.row }); setDirty(false); }
  }, [belge.data]);

  useEffect(() => {
    if (!id && !docId && rights.insert && master && draft === null) newDoc();
  }, [master]);

  const newDoc = async () => {
    if (!master) return;
    let row: Record<string, any> = {};
    try {
      row = (await api(`/screens/${screen.tag}/dizayn/${master.levelId}/new`, { body: { row: {} } })).values ?? {};
    } catch (e: any) {
      toast.error(e.message);
    }
    setDocId(null);
    setDraft(row);
    setDirty(true);
  };

  const save = async () => {
    if (!master || !draft) return;
    setSaving(true);
    try {
      const key = docId ? { [master.source?.key?.[0] ?? 'ID']: docId } : undefined;
      const r = await api(`/screens/${screen.tag}/dizayn/belge/master`, { body: { levelId: master.levelId, row: draft, key } });
      toast.success('Belge kaydedildi');
      const newId = Number(r.id ?? docId);
      setDocId(newId);
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['belge', screen.tag] });
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!docId) return;
    if (!(await confirmDialog('Belge ve tüm satırları silinecek. Silmek İstediğinizden Eminmisiniz ?', { danger: true, ok: 'Sil' }))) return;
    try {
      await api(`/screens/${screen.tag}/dizayn/belge/${docId}/delete`, { method: 'POST' });
      toast.success('Belge silindi');
      setDocId(null);
      setDraft(null);
      qc.invalidateQueries({ queryKey: ['belge', screen.tag] });
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const copy = async () => {
    if (!docId) return;
    try {
      const r = await api(`/screens/${screen.tag}/dizayn/belge/${docId}/copy`, { method: 'POST' });
      toast.success('Belge çoğaltıldı');
      if (r.newId) setDocId(Number(r.newId));
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const masterFields = useMemo(() => {
    if (!master) return [];
    const cols = dizaynColumns(master.columns, master.fields, {
      tag: screen.tag, editableCols: master.source ? new Set(Object.keys(master.source.map).map((k) => k.toLocaleUpperCase('tr-TR'))) : undefined,
    });
    return cols.filter((c) => !c.hidden);
  }, [master, screen.tag]);

  if (belge.isLoading) return <Spinner />;
  if (belge.error) return <ErrorBox error={belge.error} />;
  if (!master) return <ErrorBox error="Bu belge ekranı için Dizayn 'Master' tanımı bulunamadı" />;
  const reports = def.data?.dizayn?.reports ?? [];

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2 p-2">
      <div className="panel flex flex-wrap items-center gap-1.5 rounded px-2 py-1.5">
        <button className="btn" onClick={() => setPickOpen(true)}><FolderOpen size={14} /> Belge Seç</button>
        <button className="btn" disabled={!rights.insert} onClick={newDoc}><FilePlus2 size={14} /> Yeni Belge</button>
        <button className="btn btn-primary" disabled={saving || !draft || (!!docId ? !rights.update : !rights.insert)} onClick={save}><Save size={14} /> Kaydet</button>
        <button className="btn" disabled={!docId || !rights.insert} onClick={copy}><Copy size={14} /> Çoğalt</button>
        <button className="btn btn-danger" disabled={!docId || !rights.delete} onClick={remove}><Trash2 size={14} /> Sil</button>
        <span className="ml-2 text-xs muted">{docId ? `Belge No: ${docId}` : 'Yeni belge'}{dirty ? ' · kaydedilmedi' : ''}</span>
        <div className="ml-auto"><ReportMenu reports={reports} row={docId ? { ...(draft ?? {}), ID: docId } : null} /></div>
      </div>
      {draft && (
        <div className="panel grid gap-x-4 gap-y-2 rounded p-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
          {masterFields.map((c) => (
            <label key={c.key} className="flex flex-col gap-0.5">
              <span className="text-[11.5px] muted">{c.caption}</span>
              <div className={cn('h-7 rounded border b-border', c.readonly ? 'bg-[var(--grid-header)]' : 'bg-[var(--panel)]')}>
                <CellEditor col={c} row={draft} onChange={(p) => { setDraft((d) => ({ ...(d ?? {}), ...p })); setDirty(true); }} />
              </div>
            </label>
          ))}
        </div>
      )}
      {docId ? (
        <DetailTabs screen={screen} details={belge.data?.details ?? []} sources={belge.data?.sources ?? []} docId={docId} master={draft ?? {}} />
      ) : (
        <div className="panel rounded p-4 text-center muted">Satır girmek için önce belgeyi kaydediniz.</div>
      )}
      {pickOpen && (
        <LookupDialog open={pickOpen} onClose={() => setPickOpen(false)} row={{}}
          spec={{ title: `${screen.title} · Belge Listesi`, fetch: (q) => api(`/screens/${screen.tag}/dizayn/belge/list`, { body: { query: q } }), apply: (p) => p }}
          onPick={(p) => { const v = p.ID ?? p.Id ?? p.id ?? Object.values(p)[0]; setDocId(Number(v)); }} />
      )}
    </div>
  );
}

function DetailTabs({ screen, details, sources, docId, master }: { screen: ScreenDef; details: Part[]; sources: Source[]; docId: number; master: Record<string, any> }) {
  const [active, setActive] = useState(String(details[0]?.levelId ?? ''));
  const qc = useQueryClient();
  if (!details.length) return null;
  const detail = details[0];
  /**
   * Delphi Form81 Aktar / Hepsini Aktar: kaynak satırın 'ID' dışındaki alanlarından, detayın Database_Yonetim'inde
   * tanımlı olanlar yeni detay satırına kopyalanır ve kaydedilir (HESAPLAMA çalışır).
   */
  const transfer = async (rows: Record<string, any>[]) => {
    const defined = new Set(detail.fields.map((f) => f.field.toLocaleUpperCase('tr-TR')));
    let ok = 0;
    for (const src of rows) {
      const row: Record<string, any> = {};
      for (const [k, v] of Object.entries(src)) if (k.toUpperCase() !== 'ID' && defined.has(k.toLocaleUpperCase('tr-TR'))) row[k] = v;
      try {
        await api(`/screens/${screen.tag}/dizayn/${detail.levelId}/rows`, { body: { row, master, docId } });
        ok++;
      } catch (e: any) {
        toast.error(e.message);
        break;
      }
    }
    if (ok) toast.success(`${ok} satır aktarıldı`);
    qc.invalidateQueries({ queryKey: ['belge-detail', screen.tag] });
  };
  const sourcePanel = sources.length > 0 && (
    <SourceLists screen={screen} sources={sources} docId={docId} onTransfer={transfer} canInsert={screen.rights.insert} />
  );
  const grid = (d: Part) => {
    const editableCols = d.source ? new Set(Object.keys(d.source.map).map((k) => k.toLocaleUpperCase('tr-TR'))) : undefined;
    const keyField = d.source?.key?.[0] ?? 'ID';
    return (
      <DataGrid key={d.levelId}
        queryKey={['belge-detail', screen.tag, d.levelId, docId]}
        fetch={(q) => api(`/screens/${screen.tag}/dizayn/${d.levelId}/list`, { body: { query: q, context: { ara: docId, ID: docId } } })}
        buildColumns={(meta) => dizaynColumns(meta, d.fields, { tag: screen.tag, editableCols })}
        layoutKey={`dz:${screen.formTitle}:${d.name}`}
        editing={{
          canInsert: screen.rights.insert, canUpdate: screen.rights.update, canDelete: screen.rights.delete,
          onNewDefaults: d.hooks.new ? async (row) => (await api(`/screens/${screen.tag}/dizayn/${d.levelId}/new`, { body: { row, master } })).values : undefined,
          onSave: (row, original) => original
            ? api(`/screens/${screen.tag}/dizayn/${d.levelId}/rows`, { method: 'PUT', body: { row, key: { [keyField]: original[keyField] }, master, docId } })
            : api(`/screens/${screen.tag}/dizayn/${d.levelId}/rows`, { body: { row, master, docId } }),
          onDelete: (row) => api(`/screens/${screen.tag}/dizayn/${d.levelId}/delete`, { body: { key: { [keyField]: row[keyField] }, row, master } }),
        }}
      />
    );
  };
  if (details.length === 1) return <div className="flex min-h-0 flex-1 flex-col gap-2"><div className="flex min-h-0 flex-[3] flex-col">{grid(details[0])}</div>{sourcePanel}</div>;
  return (
    <Tabs.Root value={active} onValueChange={setActive} className="flex min-h-0 flex-1 flex-col">
      <Tabs.List className="flex gap-0.5">
        {details.map((d) => (
          <Tabs.Trigger key={d.levelId} value={String(d.levelId)} className="rounded-t border border-b-0 border-transparent px-3 py-1.5 text-[12.5px] muted data-[state=active]:b-border data-[state=active]:bg-[var(--panel)] data-[state=active]:text-[var(--text)]">
            {d.caption}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
      {details.map((d) => (
        <Tabs.Content key={d.levelId} value={String(d.levelId)} className="flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden">{grid(d)}</Tabs.Content>
      ))}
    </Tabs.Root>
  );
}

function SourceLists({ screen, sources, docId, onTransfer, canInsert }: { screen: ScreenDef; sources: Source[]; docId: number; onTransfer: (rows: Record<string, any>[]) => Promise<void>; canInsert: boolean }) {
  const [active, setActive] = useState(String(sources[0].levelId));
  const [sel, setSel] = useState<Record<string, any> | null>(null);
  const src = sources.find((x) => String(x.levelId) === active) ?? sources[0];
  const fetchAll = () => api<ListResult>(`/screens/${screen.tag}/dizayn/${src.levelId}/list`, { body: { query: { all: true }, context: { ara: docId, ID: docId } } });
  return (
    <div className="flex min-h-0 flex-[2] flex-col">
      <div className="flex items-center gap-1">
        {sources.map((x) => (
          <button key={x.levelId} className={cn('rounded-t px-3 py-1 text-[12.5px]', String(x.levelId) === active ? 'bg-[var(--panel)] font-medium border b-border border-b-0' : 'muted')} onClick={() => setActive(String(x.levelId))}>{x.caption}</button>
        ))}
        <span className="ml-2 text-xs muted">Kaynak listeler — çift tıklayarak detaya aktarın</span>
      </div>
      <DataGrid key={src.levelId}
        queryKey={['belge-source', screen.tag, src.levelId, docId]}
        fetch={(q) => api(`/screens/${screen.tag}/dizayn/${src.levelId}/list`, { body: { query: q, context: { ara: docId, ID: docId } } })}
        buildColumns={(meta) => dizaynColumns(meta, src.fields, { tag: screen.tag, editableCols: new Set() })}
        layoutKey={`dz:${screen.formTitle}:${src.name}`}
        onSelectionChange={setSel}
        onRowOpen={(r) => canInsert && onTransfer([r])}
        toolbarLeft={<>
          <button className="btn" disabled={!sel || !canInsert} onClick={() => sel && onTransfer([sel])}>Aktar</button>
          <button className="btn" disabled={!canInsert} onClick={async () => onTransfer((await fetchAll()).rows)}>Hepsini Aktar</button>
        </>}
      />
    </div>
  );
}
